import React, { useState } from 'react';

import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import useSWR from 'swr';

import {
  SearchAutocomplete,
  type ISearchSuggestionOption,
} from '@orcha/micro-library-search-ui';
import { useSiteContext } from 'src/store';
import useDebounce from 'src/utilities/useDebounce';
import { AutocompleteResponse } from 'src/pages/api/autocomplete';

import theme from '../../../themes/theme';

type SearchAutocompleteWrapperProps = {
  placeholderText?: string;
  onInputChanged?: (value: string) => void;
  onFreeTextSubmit: () => void;
};

const SearchAutocompleteWrapper = ({
  placeholderText,
  onInputChanged,
  onFreeTextSubmit,
}: SearchAutocompleteWrapperProps) => {
  const { t } = useTranslation('search');

  const [searchTerm, setSearchTerm] = useState<string>('');

  const debouncedSearch = useDebounce(searchTerm, 300);
  const { tenantId, packageId, appContainerId, countryOfUse } =
    useSiteContext();
  const router = useRouter();
  const { locale } = router;

  const fetcher = (url: string) => fetch(url).then((res) => res.json());
  const shouldGetSuggestions =
    debouncedSearch && searchTerm && searchTerm?.length > 2;

  const localeQuery = locale ? `&language=${locale}` : '';
  const countryQuery = countryOfUse ? `&country=${countryOfUse.code}` : '';
  const { data: autocompleteResponse } = useSWR<AutocompleteResponse>(
    shouldGetSuggestions
      ? `/api/autocomplete?tenantId=${tenantId}&packageId=${packageId}&containerId=${appContainerId}&searchTerm=${encodeURIComponent(
          debouncedSearch
        )}${localeQuery}${countryQuery}`
      : null,
    fetcher
  );

  const autoCompleteOptions = autocompleteResponse?.suggestions ?? [];

  const searchSuggestionOptions: ISearchSuggestionOption[] = autoCompleteOptions
    .filter((o) => o.children)
    .flatMap((option) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      // filter above
      option.children!.map((childOption) => {
        return {
          groupName: option.name,
          optionName: childOption.name,
          suggestionType: childOption.searchFilterType,
        };
      })
    );

  const onSearchSuggestionOptionSelect = (option: ISearchSuggestionOption) => {
    router.push(
      `/search?${
        option?.suggestionType ?? 'healthCategories'
      }=${encodeURIComponent(option.optionName)}`
    );
  };

  const onTextInputChanged = (value: string) => {
    setSearchTerm(value);
    if (onInputChanged) {
      onInputChanged(value);
    }
  };

  return (
    <>
      <SearchAutocomplete
        sx={{ backgroundColor: theme.palette.common.white }}
        onInputChange={(_event, value) => onTextInputChanged(value)}
        inputValue={searchTerm ?? ''}
        searchSuggestionOptions={searchSuggestionOptions ?? []}
        placeholderText={placeholderText ?? t('placeholder')}
        inputLabel={t('searchInputLabel')}
        onOptionSelect={onSearchSuggestionOptionSelect}
        optionsHeader={t('autocompleteLabel.healthcategories')}
        onFreeTextSubmit={onFreeTextSubmit}
      />
    </>
  );
};

export default SearchAutocompleteWrapper;
